import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { addDays, format, isBefore, isSameDay, startOfDay } from 'date-fns';
import { includes, isNil } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';

import { getLabelMessage } from '@gef-ui/localization/messages';
import FileExclamation from '@gef-ui/material-icons/FileExclamation';
import FileXmark from '@gef-ui/material-icons/FileXmark';

import { defaultDateFormatDateFns, EMPTY_VALUES } from 'constants/general';
import { documentTypeIdToTextMap } from 'features/views/productionControl/constants';
import { equalOrThreeDashes } from 'utils';
import { fromIsoDate } from 'utils/apiDateTime';
import InfoCircleOutlineTooltip from 'pages/ProductionControl/components/InfoCircleOutlineTooltip';

export const generateInfoCircleOutlineTooltip = ({ notice, userCreated, dateCreated }) => {
	let finalNote;
	// trim string, dont show whole note if longer than 100 chars
	const trimmedNote = isNil(notice) ? EMPTY_VALUES.STRING : notice.trim();
	if (trimmedNote.length > 100) {
		finalNote = trimmedNote.slice(0, 100).concat('...');
	} else {
		finalNote = trimmedNote;
	}

	const formattedDate = equalOrThreeDashes(fromIsoDate(dateCreated));

	const finalText = (
		<>
			<div>{`${getLabelMessage('productionControl:noticeDateCreated')}: ${formattedDate}`}</div>
			<div>{`${getLabelMessage('productionControl:noticeUserCreated')}: ${userCreated}`}</div>
			<div>{finalNote}</div>
		</>
	);

	return <InfoCircleOutlineTooltip text={finalText} color="info.main" />;
};

export const getFilledValuesForFiltersFromQueryParams = (queryParams, filters, filtersMap) => {
	if (queryParams) {
		return Object.keys(queryParams)
			.filter((key) => includes(key, filters))
			.map((key) => ({ key: filtersMap[key], value: queryParams[key] }));
	} else {
		return EMPTY_VALUES.ARRAY;
	}
};

export const generateChipWithTooltipOrThreeDashes = (documentType, documentTypeText) => {
	if (isNilOrEmpty(documentType)) {
		return equalOrThreeDashes();
	} else {
		const label = documentTypeIdToTextMap[documentType];
		return (
			<Tooltip title={documentTypeText}>
				<span>
					<Chip label={label} variant="outlined" color="error" sx={{ borderColor: 'error.main' }} />
				</span>
			</Tooltip>
		);
	}
};

export const generateDocumentStateIconWithTooltip = (documentState, documentStateText) => {
	let icon;
	if (documentState === 1) {
		icon = <FileXmark sx={{ color: 'grey.600', mt: 1 }} />;
	} else {
		icon = <FileExclamation sx={{ color: 'grey.600', mt: 1 }} />;
	}

	return (
		<Tooltip title={documentStateText}>
			<span>{icon}</span>
		</Tooltip>
	);
};

export const generateEscalationAndDueDateValues = ({ createDate, solutionStateId }) => {
	const today = startOfDay(new Date());
	const createDateResetted = startOfDay(new Date(createDate));

	const createdDatePlus10Days = addDays(createDateResetted, 10);
	const createdDatePlus15Days = addDays(createDateResetted, 15);
	const createdDatePlus18Days = addDays(createDateResetted, 18);
	const result = {};

	if (solutionStateId === 1) {
		if (isBefore(today, createdDatePlus10Days) || isSameDay(today, createdDatePlus10Days)) {
			// on time
			result.escalation = getLabelMessage('productionControl:onTime');
			result.dueDate = format(createdDatePlus10Days, defaultDateFormatDateFns);
		} else {
			if (isBefore(today, createdDatePlus15Days) || isSameDay(today, createdDatePlus15Days)) {
				// 1. escalation
				result.escalation = getLabelMessage('productionControl:firstEscalation');
				result.dueDate = format(createdDatePlus15Days, defaultDateFormatDateFns);
			} else {
				// 2. escalation
				result.escalation = getLabelMessage('productionControl:secondEscalation');
				result.dueDate = format(createdDatePlus18Days, defaultDateFormatDateFns);
			}
		}
	} else {
		result.escalation = getLabelMessage('productionControl:completed');
		result.dueDate = equalOrThreeDashes;
	}

	return result;
};
